import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  FiFilter, FiChevronUp, FiChevronDown, FiChevronRight, FiChevronLeft,
  FiColumns, FiDownload, FiX, FiSearch, FiInbox, FiMaximize2, FiMinimize2,
} from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { formatDateMedium } from '../../utils/dateFormat';
import { formatNumber } from '../../utils/numberFormat';
import SearchableSelect from '../SearchableSelect';
import useStickyTableHeader from '../../hooks/useStickyTableHeader';
import useFullscreenPanel from '../../hooks/useFullscreenPanel';
import './DataGrid.scss';

/**
 * Generic client-side data grid: quick search, per-column header filters,
 * asc/desc sorting, grouping, column show/hide, Excel export, pagination.
 *
 * Column def:
 *   {
 *     key,                      // unique id
 *     label,                    // already-translated header text
 *     type,                     // 'text' | 'enum' | 'number' | 'date'
 *     accessor(row),            // raw value driving filter/sort/group/search
 *     render?(row),             // custom cell JSX (defaults to formatted accessor)
 *     optionLabel?(value),      // display label for enum values (filters/groups)
 *     groupable?, rightAlign?,  // flags
 *     noSort?, noFilter?, noExport?,
 *   }
 *
 * Pipeline: search → column filters → sort → (group | paginate).
 * Grouping shows all matching rows; pagination applies when not grouped.
 */

const isFilterActive = (f) => {
  if (f == null) return false;
  if (typeof f === 'string') return f.trim() !== '';
  if (Array.isArray(f)) return f.length > 0;
  return Object.values(f).some((v) => v !== '' && v != null);
};

function DataGrid({
  columns,
  rows,
  loading,
  storageKey,
  defaultVisible,
  exportName = 'export',
  searchPlaceholder,
  onRowClick,
  pageSizeOptions = [10, 25, 50],
  emptyText,
}) {
  const { t } = useTranslation();

  const allKeys = useMemo(() => columns.map((c) => c.key), [columns]);
  const [visibleKeys, setVisibleKeys] = useState(() => {
    const fallback = defaultVisible || allKeys;
    if (!storageKey) return fallback;
    try {
      const raw = window.localStorage?.getItem(storageKey);
      if (!raw) return fallback;
      const parsed = JSON.parse(raw).filter((k) => allKeys.includes(k));
      return parsed.length ? parsed : fallback;
    } catch { return fallback; }
  });
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState(null);          // { key, dir: 1 | -1 }
  const [groupBy, setGroupBy] = useState('');
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeOptions[0]);
  const [openMenu, setOpenMenu] = useState(null);  // 'cols' | `filter:${key}`
  const [menuPos, setMenuPos] = useState(null);

  // The table grows with its content — vertical navigation is the page's own
  // scroll. Only the horizontal axis scrolls inside the box, and the header
  // stays visible through a pinned copy above it, re-measured whenever the
  // column set or the row count changes the table's shape.
  const stickyHead = useStickyTableHeader([visibleKeys.join('|'), (rows || []).length]);

  // Dense working view: the grid becomes a fixed panel over the whole viewport
  // and owns its own scrolling. State lives here, so nothing is lost on the
  // way in or out.
  const [fullscreen, setFullscreen] = useFullscreenPanel();

  useEffect(() => {
    if (!storageKey) return;
    try { window.localStorage?.setItem(storageKey, JSON.stringify(visibleKeys)); } catch { /* noop */ }
  }, [visibleKeys, storageKey]);

  // Back to page 1 whenever the visible set changes shape.
  useEffect(() => { setPage(1); }, [search, filters, groupBy, pageSize, rows]);

  const toggleMenu = (id, e) => {
    if (openMenu === id) { setOpenMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({
      top: Math.min(rect.bottom + 6, window.innerHeight - 320),
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 290)),
    });
    setOpenMenu(id);
  };

  useEffect(() => {
    if (!openMenu) return;
    const inMenuOrTrigger = (target) =>
      target instanceof Element
      && (target.closest('.dg-menu') || target.closest('[data-dg-trigger]'));
    const onDown = (e) => { if (!inMenuOrTrigger(e.target)) setOpenMenu(null); };
    const onKey = (e) => { if (e.key === 'Escape') setOpenMenu(null); };
    const onScroll = (e) => {
      if (e.target instanceof Element && e.target.closest('.dg-menu')) return;
      setOpenMenu(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [openMenu]);

  const visibleCols = useMemo(
    () => columns.filter((c) => visibleKeys.includes(c.key)),
    [columns, visibleKeys]
  );

  // Formatted string for a cell — used by group labels, search and export.
  const displayValue = useCallback((col, row) => {
    const raw = col.accessor(row);
    if (raw == null || raw === '') return '';
    if (col.optionLabel) return col.optionLabel(raw) ?? '';
    if (col.type === 'date') return formatDateMedium(raw) || '';
    if (col.type === 'number') return formatNumber(raw, String(raw));
    return String(raw);
  }, []);

  const distinctValues = useMemo(() => {
    const map = {};
    for (const col of columns) {
      if (col.type !== 'enum') continue;
      const seen = new Set();
      for (const r of rows || []) {
        const v = col.accessor(r);
        if (v !== '' && v != null) seen.add(v);
      }
      map[col.key] = [...seen].sort((a, b) => String(a).localeCompare(String(b)));
    }
    return map;
  }, [columns, rows]);

  // ── Pipeline ──
  const searchedRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows || [];
    return (rows || []).filter((r) =>
      visibleCols.some((col) => displayValue(col, r).toLowerCase().includes(q)));
  }, [rows, search, visibleCols, displayValue]);

  const filteredRows = useMemo(() => {
    let out = searchedRows;
    for (const [key, f] of Object.entries(filters)) {
      if (!isFilterActive(f)) continue;
      const col = columns.find((c) => c.key === key);
      if (!col) continue;
      out = out.filter((r) => {
        const raw = col.accessor(r);
        switch (col.type) {
          case 'text':
            return String(raw || '').toLowerCase().includes(f.trim().toLowerCase());
          case 'enum':
            return f.includes(raw);
          case 'number': {
            if (raw == null) return false;
            const n = Number(raw);
            if (f.min !== '' && f.min != null && n < Number(f.min)) return false;
            if (f.max !== '' && f.max != null && n > Number(f.max)) return false;
            return true;
          }
          case 'date': {
            if (!raw) return false;
            const d = new Date(raw).getTime();
            if (f.from && d < new Date(f.from).getTime()) return false;
            if (f.to && d > new Date(f.to).getTime() + 24 * 60 * 60 * 1000 - 1) return false;
            return true;
          }
          default: return true;
        }
      });
    }
    return out;
  }, [searchedRows, filters, columns]);

  const sortedRows = useMemo(() => {
    if (!sort) return filteredRows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      const va = col.accessor(a); const vb = col.accessor(b);
      if (va == null || va === '') return 1;
      if (vb == null || vb === '') return -1;
      let cmp;
      if (col.type === 'number') cmp = Number(va) - Number(vb);
      else if (col.type === 'date') cmp = new Date(va) - new Date(vb);
      else cmp = String(va).localeCompare(String(vb), undefined, { sensitivity: 'base' });
      return cmp * sort.dir;
    });
  }, [filteredRows, sort, columns]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedRows = useMemo(() => {
    if (groupBy) return sortedRows; // grouped view shows everything
    const start = (safePage - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, groupBy, safePage, pageSize]);

  const groups = useMemo(() => {
    if (!groupBy) return null;
    const col = columns.find((c) => c.key === groupBy);
    if (!col) return null;
    const map = new Map();
    for (const r of sortedRows) {
      const label = displayValue(col, r) || t('contracts.table.ungrouped', { defaultValue: '(empty)' });
      if (!map.has(label)) map.set(label, []);
      map.get(label).push(r);
    }
    return [...map.entries()].map(([label, groupRows]) => ({ label, rows: groupRows }));
  }, [groupBy, sortedRows, columns, displayValue, t]);

  const activeFilterCount = Object.values(filters).filter(isFilterActive).length;
  const groupableCols = columns.filter((c) => c.groupable);

  const cycleSort = (key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 1 };
      if (prev.dir === 1) return { key, dir: -1 };
      return null;
    });
  };

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const clearFilter = (key) => setFilters((prev) => {
    const next = { ...prev };
    delete next[key];
    return next;
  });

  const toggleGroup = (label) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };

  const exportExcel = () => {
    const cols = visibleCols.filter((c) => !c.noExport);
    const header = cols.map((c) => c.label);
    const rowToCells = (r) => cols.map((col) => {
      if (col.type === 'number') {
        const raw = col.accessor(r);
        return raw != null && raw !== '' ? Number(raw) : '';
      }
      return displayValue(col, r);
    });
    const aoa = [header];
    if (groups) {
      const groupCol = columns.find((c) => c.key === groupBy);
      for (const g of groups) {
        aoa.push([`${groupCol?.label || ''}: ${g.label} (${g.rows.length})`]);
        for (const r of g.rows) aoa.push(rowToCells(r));
      }
    } else {
      for (const r of sortedRows) aoa.push(rowToCells(r));
    }
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = cols.map((col) => ({ wch: col.type === 'text' ? 28 : 16 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, exportName.slice(0, 31));
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `${exportName}_${stamp}.xlsx`);
  };

  const renderFilterMenu = (col) => {
    const f = filters[col.key];
    return (
      <div
        className="dg-menu"
        style={menuPos ? { top: menuPos.top, left: menuPos.left } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {col.type === 'text' && (
          <input
            type="text"
            className="dg-menu__input"
            placeholder={t('contracts.table.contains', { defaultValue: 'Contains…' })}
            value={f || ''}
            autoFocus
            onChange={(e) => setFilter(col.key, e.target.value)}
          />
        )}
        {col.type === 'enum' && (
          <div className="dg-menu__list">
            {(distinctValues[col.key] || []).length === 0 && (
              <span className="dg-menu__empty">{t('contracts.table.noValues', { defaultValue: 'No values' })}</span>
            )}
            {(distinctValues[col.key] || []).map((v) => {
              const selected = Array.isArray(f) && f.includes(v);
              const label = col.optionLabel ? (col.optionLabel(v) ?? String(v)) : String(v);
              return (
                <label key={String(v)} className="dg-menu__option">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => {
                      const cur = Array.isArray(f) ? f : [];
                      setFilter(col.key, selected ? cur.filter((x) => x !== v) : [...cur, v]);
                    }}
                  />
                  <span>{label}</span>
                </label>
              );
            })}
          </div>
        )}
        {col.type === 'number' && (
          <div className="dg-menu__range">
            <input
              type="number"
              className="dg-menu__input"
              placeholder={t('contracts.table.min', { defaultValue: 'Min' })}
              value={f?.min ?? ''}
              onChange={(e) => setFilter(col.key, { ...(f || {}), min: e.target.value })}
            />
            <input
              type="number"
              className="dg-menu__input"
              placeholder={t('contracts.table.max', { defaultValue: 'Max' })}
              value={f?.max ?? ''}
              onChange={(e) => setFilter(col.key, { ...(f || {}), max: e.target.value })}
            />
          </div>
        )}
        {col.type === 'date' && (
          <div className="dg-menu__range">
            <input
              type="date"
              className="dg-menu__input"
              value={f?.from || ''}
              onChange={(e) => setFilter(col.key, { ...(f || {}), from: e.target.value })}
            />
            <input
              type="date"
              className="dg-menu__input"
              value={f?.to || ''}
              onChange={(e) => setFilter(col.key, { ...(f || {}), to: e.target.value })}
            />
          </div>
        )}
        {isFilterActive(f) && (
          <button type="button" className="dg-menu__clear" onClick={() => clearFilter(col.key)}>
            <FiX /> {t('contracts.table.clearFilter', { defaultValue: 'Clear filter' })}
          </button>
        )}
      </div>
    );
  };

  const renderCell = (col, r) => {
    if (col.render) return col.render(r);
    const label = displayValue(col, r);
    return label
      ? <span className="dg-cell-text">{label}</span>
      : <span className="dg-cell-muted">—</span>;
  };

  const renderRow = (r, idx) => (
    <tr
      key={r._id || idx}
      className={`dg-row ${onRowClick ? 'dg-row--clickable' : ''}`}
      onClick={onRowClick ? () => onRowClick(r) : undefined}
    >
      {visibleCols.map((col) => (
        <td key={col.key} className={col.rightAlign ? 'dg-td--right' : ''}>
          {renderCell(col, r)}
        </td>
      ))}
    </tr>
  );

  const colCount = visibleCols.length;
  const showSkeleton = loading && (rows || []).length === 0;

  // One header row, rendered twice: in the table itself, and in the pinned bar
  // above it. `pinned` marks the copy — it must not render the filter popovers
  // a second time (they are portalled and would double up), and it stays out
  // of the tab order because the real header is already in it.
  const renderHeadRow = (pinned) => (
    <tr>
      {visibleCols.map((col) => {
        const sorted = sort?.key === col.key;
        const filtered = isFilterActive(filters[col.key]);
        return (
          <th
            key={col.key}
            className={col.rightAlign ? 'dg-th--right' : ''}
            aria-sort={sorted ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}
          >
            <div className="dg-th">
              {col.noSort ? (
                <span className="dg-th__label">{col.label}</span>
              ) : (
                <button
                  type="button"
                  className="dg-th__sort"
                  tabIndex={pinned ? -1 : undefined}
                  onClick={() => cycleSort(col.key)}
                >
                  <span>{col.label}</span>
                  <span className={`dg-th__sort-icons ${sorted ? 'dg-th__sort-icons--on' : ''}`}>
                    {sorted ? (sort.dir === 1 ? <FiChevronUp /> : <FiChevronDown />) : <FiChevronDown />}
                  </span>
                </button>
              )}
              {!col.noFilter && (
                <span className="dg-th__filter-anchor">
                  <button
                    type="button"
                    data-dg-trigger
                    className={`dg-th__filter ${filtered ? 'dg-th__filter--on' : ''}`}
                    tabIndex={pinned ? -1 : undefined}
                    onClick={(e) => toggleMenu(`filter:${col.key}`, e)}
                    aria-label={t('contracts.table.filterCol', { col: col.label, defaultValue: `Filter ${col.label}` })}
                  >
                    <FiFilter />
                  </button>
                  {!pinned && openMenu === `filter:${col.key}`
                    && createPortal(renderFilterMenu(col), document.body)}
                </span>
              )}
            </div>
          </th>
        );
      })}
    </tr>
  );

  const grid = (
    <div
      className={[
        'dg',
        loading && (rows || []).length > 0 ? 'dg--loading' : '',
        fullscreen ? 'dg--fs' : '',
      ].filter(Boolean).join(' ')}
    >
      {/* Toolbar: search / group / filters-clear | columns / export */}
      <div className="dg-toolbar">
        <div className="dg-toolbar__left">
          <div className="dg-search">
            <FiSearch />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder || t('common.search', { defaultValue: 'Search…' })}
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} aria-label={t('common.clearFilters')}>
                <FiX />
              </button>
            )}
          </div>
          {groupableCols.length > 0 && (
            <div className="dg-toolbar__group">
              <SearchableSelect
                value={groupBy}
                onChange={(v) => { setGroupBy(v || ''); setCollapsed(new Set()); }}
                placeholder={t('contracts.table.groupBy', { defaultValue: 'Group by' })}
                options={[
                  { value: '', label: t('contracts.table.noGrouping', { defaultValue: 'No grouping' }) },
                  ...groupableCols.map((c) => ({
                    value: c.key,
                    label: t('contracts.table.groupByCol', { col: c.label, defaultValue: `Group: ${c.label}` }),
                  })),
                ]}
              />
            </div>
          )}
          {activeFilterCount > 0 && (
            <button type="button" className="dg-clear-filters" onClick={() => setFilters({})}>
              <FiX />
              <span>{t('contracts.table.clearColumnFilters', { count: activeFilterCount, defaultValue: `Clear filters (${activeFilterCount})` })}</span>
            </button>
          )}
        </div>
        <div className="dg-toolbar__right">
          <button
            type="button"
            data-dg-trigger
            className={`dg-btn ${openMenu === 'cols' ? 'dg-btn--pressed' : ''}`}
            onClick={(e) => toggleMenu('cols', e)}
          >
            <FiColumns />
            <span>{t('contracts.table.columns', { defaultValue: 'Columns' })}</span>
          </button>
          {openMenu === 'cols' && createPortal(
            <div className="dg-menu" style={menuPos ? { top: menuPos.top, left: menuPos.left } : undefined}>
              <div className="dg-menu__list">
                {columns.map((c) => (
                  <label key={c.key} className="dg-menu__option">
                    <input
                      type="checkbox"
                      checked={visibleKeys.includes(c.key)}
                      disabled={visibleKeys.includes(c.key) && visibleKeys.length === 1}
                      onChange={() => {
                        setVisibleKeys((prev) => prev.includes(c.key)
                          ? prev.filter((k) => k !== c.key)
                          : allKeys.filter((k) => prev.includes(k) || k === c.key));
                      }}
                    />
                    <span>{c.label}</span>
                  </label>
                ))}
              </div>
              <button
                type="button"
                className="dg-menu__clear"
                onClick={() => setVisibleKeys(defaultVisible || allKeys)}
              >
                {t('contracts.table.resetColumns', { defaultValue: 'Reset columns' })}
              </button>
            </div>,
            document.body
          )}
          <button
            type="button"
            className="dg-btn"
            onClick={exportExcel}
            disabled={showSkeleton || sortedRows.length === 0}
          >
            <FiDownload />
            <span>{t('contracts.table.exportExcel', { defaultValue: 'Export to Excel' })}</span>
          </button>
          <button
            type="button"
            className={`dg-btn ${fullscreen ? 'dg-btn--pressed' : ''}`}
            onClick={() => setFullscreen((on) => !on)}
            title={fullscreen
              ? t('contracts.table.exitFullscreen', { defaultValue: 'Exit full screen (Esc)' })
              : t('contracts.table.fullscreen', { defaultValue: 'Full screen' })}
          >
            {fullscreen ? <FiMinimize2 /> : <FiMaximize2 />}
            <span>
              {fullscreen
                ? t('contracts.table.exitFullscreen', { defaultValue: 'Exit full screen' })
                : t('contracts.table.fullscreen', { defaultValue: 'Full screen' })}
            </span>
          </button>
        </div>
      </div>

      {/* The pinned copy of the header. Same cells, same handlers — it is a
          re-render, not a DOM clone — laid out to the measured column widths
          and slid sideways with the table's own scroll. Hidden until the real
          header has left the top of the viewport.

          Full screen does not need it: there the scroll box is the viewport
          panel itself, so the real `thead` can stick to it natively. */}
      {!fullscreen && (
        <div
          className={`dg-sticky-head ${stickyHead.active ? 'is-on' : ''}`}
          style={{ marginBottom: stickyHead.headHeight ? -stickyHead.headHeight : 0 }}
          aria-hidden="true"
        >
          <div className="dg-sticky-head__inner" ref={stickyHead.innerRef}>
            <table
              className="dg-table dg-table--pinned"
              style={stickyHead.tableWidth ? { width: stickyHead.tableWidth } : undefined}
            >
              <colgroup>
                {stickyHead.widths.map((w, i) => (
                  <col key={visibleCols[i]?.key || i} style={{ width: w }} />
                ))}
              </colgroup>
              <thead>{renderHeadRow(true)}</thead>
            </table>
          </div>
        </div>
      )}

      <div className="dg-scroll" ref={stickyHead.scrollRef}>
        <table className="dg-table">
          <thead ref={stickyHead.headRef}>{renderHeadRow(false)}</thead>
          <tbody>
            {showSkeleton && Array.from({ length: 6 }).map((_, i) => (
              <tr key={i} className="dg-skeleton-row">
                {Array.from({ length: colCount }).map((__, j) => (
                  <td key={j}><div className="dg-skeleton" /></td>
                ))}
              </tr>
            ))}
            {!showSkeleton && sortedRows.length === 0 && (
              <tr>
                <td colSpan={colCount}>
                  <div className="dg-empty">
                    <FiInbox />
                    <span>{emptyText || t('contracts.table.noFilterMatches', { defaultValue: 'No records match the current filters.' })}</span>
                  </div>
                </td>
              </tr>
            )}
            {!showSkeleton && !groups && pagedRows.map((r, idx) => renderRow(r, idx))}
            {!showSkeleton && groups && groups.map((g) => (
              <React.Fragment key={g.label}>
                <tr className="dg-group-row" onClick={() => toggleGroup(g.label)}>
                  <td colSpan={colCount}>
                    <div className="dg-group-row__inner">
                      <span className={`dg-group-row__chevron ${collapsed.has(g.label) ? '' : 'dg-group-row__chevron--open'}`}>
                        <FiChevronRight />
                      </span>
                      <span className="dg-group-row__label">{g.label}</span>
                      <span className="dg-group-row__count">{g.rows.length}</span>
                    </div>
                  </td>
                </tr>
                {!collapsed.has(g.label) && g.rows.map((r, idx) => renderRow(r, idx))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* The table's own scrollbar would sit at the bottom of a page-length
          table. This one rides the fold instead, and drives it. Full screen
          has a real bottom edge, so the native scrollbar is enough there. */}
      {!fullscreen && stickyHead.overflowing && (
        <div className="dg-hscroll" ref={stickyHead.barRef}>
          <div className="dg-hscroll__spacer" style={{ width: stickyHead.tableWidth }} />
        </div>
      )}

      {!showSkeleton && !groupBy && sortedRows.length > 0 && (
        <div className="dg-footer">
          <span className="dg-footer__info">
            {t('contracts.showingResults', {
              start: (safePage - 1) * pageSize + 1,
              end: Math.min(safePage * pageSize, sortedRows.length),
              total: sortedRows.length,
              defaultValue: `${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, sortedRows.length)} of ${sortedRows.length}`,
            })}
          </span>
          <div className="dg-footer__controls">
            <button
              type="button"
              className="dg-page-btn"
              disabled={safePage === 1}
              onClick={() => setPage(safePage - 1)}
              aria-label={t('common.previous')}
            >
              <FiChevronLeft />
            </button>
            <span className="dg-footer__page">{safePage} / {totalPages}</span>
            <button
              type="button"
              className="dg-page-btn"
              disabled={safePage === totalPages}
              onClick={() => setPage(safePage + 1)}
              aria-label={t('common.next')}
            >
              <FiChevronRight />
            </button>
          </div>
          <div className="dg-footer__pagesize">
            <SearchableSelect
              value={String(pageSize)}
              onChange={(v) => setPageSize(Number(v))}
              options={pageSizeOptions.map((n) => ({
                value: String(n),
                label: t('contracts.table.rowsPerPage', { count: n, defaultValue: `${n} / page` }),
              }))}
            />
          </div>
        </div>
      )}
    </div>
  );

  // In full screen the grid is a fixed panel, so it is portalled out of the
  // page's stacking and overflow context. This component keeps its state
  // either way — only the DOM parent changes.
  return fullscreen ? createPortal(grid, document.body) : grid;
}

export default DataGrid;
